import { uniqBy, zip } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Popover } from '@myua/kit';
import { filterConditionApplied } from '../../../../../../general/modules/filter/actions';
import { EnumTuner } from '../../../../../../general/modules/filter/components/Tuner/EnumTuner';
import { Type } from '../../../../../../general/modules/filter/dataModel';
import { FMColumn } from '../../../model';
import { getActiveFilters, getNodes } from '../../../selectors/selectors';
import { Label } from './Label';

export function FilterEnumContainer({ column }: { column: FMColumn<Type.ID> }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const currentActiveFilter = useSelector(getActiveFilters)[column.key];
  const [checked, setChecked] = useState<Set<ReturnType<typeof column.getter>>>(new Set());
  const allData = useSelector(getNodes);
  const dispatch = useDispatch();

  const onCancel = useMemo(
    () => () => {
      const en = currentActiveFilter?.enum ?? [];
      setChecked(new Set(en));
      setOpen(false);
      setSearch('');
    },
    [currentActiveFilter],
  );

  useEffect(() => {
    onCancel();
  }, [onCancel]);

  const values = useMemo(() => {
    const { multiOptions } = column;
    const pairs: [string | number | null | undefined, string | null | undefined][] = multiOptions
      ? allData.flatMap((row) =>
          multiOptions(row).map(({ id, title }): [typeof id, string] => [id, title]),
        )
      : zip(allData.map(column.getter), allData.map(column.toDisplayString));

    const uniqFiltered = uniqBy(pairs, (pair) => pair[0]).filter(
      (pair): pair is [string | number | null, string | null] => {
        const [p0, p1] = pair;
        return p0 !== undefined && p1 !== undefined;
      },
    );

    let options = uniqFiltered
      .map(([id, title]) => ({
        id,
        title: title ?? '<empty>',
      }))
      .filter((o) => {
        return o.title.toLocaleLowerCase().includes(search.toLocaleLowerCase());
      });

    options.sort((a, b) => a.title.localeCompare(b.title));

    const initialChecked = new Set(currentActiveFilter?.enum ?? []);
    options = [
      ...options.filter(({ id }) => initialChecked.has(id)),
      ...options.filter(({ id }) => !initialChecked.has(id)),
    ];

    return options;
  }, [allData, column, search, currentActiveFilter]);

  const onApply = useCallback(() => {
    const { multiGetter } = column;
    const existingIds = new Set(
      multiGetter ? allData.flatMap((row) => multiGetter(row)) : allData.map(column.getter),
    );
    const ids = Array.from(checked.keys()).filter((id) => existingIds.has(id));
    dispatch(filterConditionApplied({ fieldID: column.key, condition: { enum: ids } }));
    setOpen(false);
    setSearch('');
  }, [checked, column, dispatch, allData]);

  const checkedItems = useMemo(() => {
    const keys = Array.from(checked.keys());
    return Object.fromEntries(keys.map((k) => [k, true]));
  }, [checked]);

  return (
    <div
      className="ch-file-manager__filter"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Popover
        trigger="click"
        placement="bottomRight"
        // Semantic звал onOpen и onClose раздельно; у antd одна ручка,
        // закрытие по-прежнему означает отмену правки фильтра.
        onOpenChange={(isOpen) => (isOpen ? setOpen(true) : onCancel())}
        open={open}
        // Закрытый поповер не держим смонтированным: под display: none
        // CellMeasurer в EnumTuner меряет строки нулевой высотой, и при
        // следующем открытии пункты списка слипаются.
        destroyOnHidden
        content={
          <EnumTuner
            searchResultItems={values}
            applyOnClick={onApply}
            checkedItems={checkedItems}
            closeOnClick={onCancel}
            isSingle={false}
            itemOnSwitch={() => {
              // not unexpected empty arrow func
            }}
            itemsOnChange={(ids: string[], v: boolean) => {
              setChecked((old) => {
                const newChecked = new Set(old);
                if (v) {
                  ids.forEach((id) => newChecked.add(id));
                } else {
                  ids.forEach((id) => newChecked.delete(id));
                }
                return newChecked;
              });
            }}
            searchOnChange={(e) => {
              setSearch(e.target.value);
            }}
            searchText={search}
          />
        }
      >
        <Label columnKey={column.key} />
      </Popover>
    </div>
  );
}
